import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { policiesService } from "api/services/policies.service";
import ContentHeader from "components/Layout/ContentHeader";
import PolicyFormModal from "components/product-group/PolicyFormModal";
import Button from "components/UI/Button";
import Table, { Column } from "components/UI/Table";
import Tabs from "components/UI/Tabs";

import type { AfterServicePolicyCreateRequest, AfterServicePolicyFull, PolicyOptionsResponse, ReturnExchangePolicyCreateRequest, ReturnExchangePolicyFull, ShippingPolicyCreateRequest, ShippingPolicyFull } from "api/types/policies.types";
import type { PolicyFormPayload, PolicyFormType } from "components/product-group/PolicyFormModal";
import "pages/AdminPage.scss";

type TabKey = PolicyFormType;

const TAB_ITEMS = [
  { key: "shipping", label: "배송정책" },
  { key: "return-exchange", label: "반품/교환정책" },
  { key: "after-service", label: "A/S정책" },
];

const formatDate = (iso: string) => iso.slice(0, 10);

const PolicyManagement: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>("shipping");
  const [options, setOptions] = useState<PolicyOptionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const fetchOptions = async () => {
    setLoading(true);
    try {
      const res = await policiesService.getOptions();
      setOptions(res.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  const handleCreate = async (payload: PolicyFormPayload) => {
    try {
      if (activeTab === "shipping") {
        await policiesService.createShipping(payload as ShippingPolicyCreateRequest);
      } else if (activeTab === "return-exchange") {
        await policiesService.createReturnExchange(payload as ReturnExchangePolicyCreateRequest);
      } else {
        await policiesService.createAfterService(payload as AfterServicePolicyCreateRequest);
      }
      setShowModal(false);
      fetchOptions();
    } catch {
      // ignore
    }
  };

  /* ── 배송 정책 테이블 ── */
  const shippingColumns: Column<ShippingPolicyFull>[] = [
    { key: "id", title: "ID", width: "60px" },
    { key: "name", title: "정책명" },
    {
      key: "label",
      title: "라벨",
      render: (v) => <span style={{ color: "#71717A", fontSize: 13 }}>{String(v ?? "-")}</span>,
    },
    {
      key: "base_fee",
      title: "기본 배송비",
      width: "120px",
      render: (v) => `${Number(v).toLocaleString()}원`,
    },
    {
      key: "pay_type",
      title: "결제 방식",
      width: "90px",
      render: (v) => (v === "prepaid" ? "선불" : v === "cod" ? "착불" : "혼합"),
    },
    {
      key: "created_at",
      title: "등록일",
      width: "110px",
      render: (v) => formatDate(String(v)),
    },
  ];

  /* ── 반품/교환 정책 테이블 ── */
  const returnColumns: Column<ReturnExchangePolicyFull>[] = [
    { key: "id", title: "ID", width: "60px" },
    { key: "name", title: "정책명" },
    {
      key: "label",
      title: "라벨",
      render: (v) => <span style={{ color: "#71717A", fontSize: 13 }}>{String(v ?? "-")}</span>,
    },
    {
      key: "return_fee_oneway",
      title: "반품비(편도)",
      width: "120px",
      render: (v) => `${Number(v).toLocaleString()}원`,
    },
    {
      key: "exchange_fee_round",
      title: "교환비(왕복)",
      width: "120px",
      render: (v) => `${Number(v).toLocaleString()}원`,
    },
    {
      key: "created_at",
      title: "등록일",
      width: "110px",
      render: (v) => formatDate(String(v)),
    },
  ];

  /* ── A/S 정책 테이블 ── */
  const asColumns: Column<AfterServicePolicyFull>[] = [
    { key: "id", title: "ID", width: "60px" },
    { key: "name", title: "정책명" },
    {
      key: "label",
      title: "라벨",
      render: (v) => <span style={{ color: "#71717A", fontSize: 13 }}>{String(v ?? "-")}</span>,
    },
    {
      key: "phone",
      title: "전화번호",
      width: "140px",
      render: (v) => String(v ?? "-"),
    },
    {
      key: "warranty_period",
      title: "보증 기간",
      width: "110px",
      render: (v) => String(v ?? "-"),
    },
    {
      key: "created_at",
      title: "등록일",
      width: "110px",
      render: (v) => formatDate(String(v)),
    },
  ];

  const tabCounts = options
    ? {
        shipping: options.shipping_policies.length,
        "return-exchange": options.return_exchange_policies.length,
        "after-service": options.after_service_policies.length,
      }
    : { shipping: 0, "return-exchange": 0, "after-service": 0 };

  return (
    <div className="admin-page">
      <ContentHeader
        title="상품 정책 관리"
        description="배송, 반품/교환, A/S 정책을 등록하고 관리합니다."
        actions={[
          <Button key="add" variant="primary" size="lg" onClick={() => setShowModal(true)}>
            + 정책 등록
          </Button>,
        ]}
      />

      <div className="admin-page__card">
        <div className="admin-page__filter">
          <Tabs
            items={TAB_ITEMS.map((t) => ({
              ...t,
              count: tabCounts[t.key as TabKey],
            }))}
            activeKey={activeTab}
            onChange={(k) => setActiveTab(k as TabKey)}
          />
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#71717A" }}>불러오는 중...</div>
        ) : (
          <>
            {activeTab === "shipping" && <Table columns={shippingColumns} data={options?.shipping_policies ?? []} rowKey="id" onRowClick={(row) => navigate(`/policy-management/shipping/${row.id}`)} />}
            {activeTab === "return-exchange" && <Table columns={returnColumns} data={options?.return_exchange_policies ?? []} rowKey="id" onRowClick={(row) => navigate(`/policy-management/return-exchange/${row.id}`)} />}
            {activeTab === "after-service" && <Table columns={asColumns} data={options?.after_service_policies ?? []} rowKey="id" onRowClick={(row) => navigate(`/policy-management/after-service/${row.id}`)} />}
          </>
        )}
      </div>

      <PolicyFormModal open={showModal} type={activeTab} submitLabel="등록" onClose={() => setShowModal(false)} onSubmit={handleCreate} />
    </div>
  );
};

export default PolicyManagement;
